import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type FeatureFlagManagerContextValue = {
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  closeWhenToggled: boolean;
  setCloseWhenToggled: (checked: boolean) => void;
};

const FeatureFlagManagerContext =
  createContext<FeatureFlagManagerContextValue | null>(null);

export const useFeatureFlagManager = () => {
  const context = useContext(FeatureFlagManagerContext);
  if (!context) {
    throw new Error(
      'useFeatureFlagManager must be used within FeatureFlagManagerProvider',
    );
  }
  return context;
};

type FeatureFlagManagerProviderProps = {
  children: React.ReactNode;
  enabled?: boolean;
};

export const FeatureFlagManagerProvider = ({
  children,
  enabled = true,
}: FeatureFlagManagerProviderProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [closeWhenToggled, setCloseWhenToggledState] = useState(() => {
    if (!enabled) {
      return false;
    }
    try {
      const stored = window.localStorage.getItem('ole_feature_flag_manager');
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.code === 'Space') {
        event.preventDefault();
        setIsModalOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    window.localStorage.setItem(
      'ole_feature_flag_manager',
      JSON.stringify(closeWhenToggled),
    );
  }, [closeWhenToggled, enabled]);

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);
  const setCloseWhenToggled = useCallback(
    (checked: boolean) => setCloseWhenToggledState(checked),
    [],
  );

  const value = useMemo(
    () => ({
      isModalOpen: enabled ? isModalOpen : false,
      openModal,
      closeModal,
      closeWhenToggled: enabled ? closeWhenToggled : false,
      setCloseWhenToggled,
    }),
    [
      closeModal,
      closeWhenToggled,
      enabled,
      isModalOpen,
      openModal,
      setCloseWhenToggled,
    ],
  );

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <FeatureFlagManagerContext.Provider value={value}>
      {children}
    </FeatureFlagManagerContext.Provider>
  );
};
